const STATUS_THRESHOLDS = {
  "90%": { safe: 10, overdue: 14 },
  "75%": { safe: 28, overdue: 35 },
  "50%": { safe: 49, overdue: 56 },
};

// Próximo escalón del downgrade automático (mismo salto que hace el cron
// downgrade_pipeline_status, docs/dominios/downgrade-pipeline.md) — usado por getDowngradeInfo
// para saber A QUÉ estatus baja una oportunidad, no solo en cuántos días.
const NEXT_ESTATUS = { "90%": "75%", "75%": "50%", "50%": "0%" };

const ACTIVE_ESTATUS = new Set(["90%", "75%", "50%"]);

export const formatDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const daysBetween = (fromDate, toDate) => {
  const from = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const to = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
  return Math.floor((to - from) / (1000 * 60 * 60 * 24));
};

// Resuelve las fechas válidas (parseadas) de una oportunidad. Lógica única de
// parseo, reusada por getOportunidadDateStatus y getOportunidadRefDate.
const resolveDates = (oportunidad) => {
  const createdRaw = oportunidad?.created_at;
  const updatedRaw = oportunidad?.updated_at;
  const createdDate = createdRaw ? new Date(createdRaw) : null;
  const updatedDate = updatedRaw ? new Date(updatedRaw) : null;
  return {
    validCreated: createdDate && !Number.isNaN(createdDate.getTime()) ? createdDate : null,
    validUpdated: updatedDate && !Number.isNaN(updatedDate.getTime()) ? updatedDate : null,
  };
};

/**
 * Fecha de referencia para "antigüedad" de una oportunidad: la de última
 * actualización (updated_at) si es válida; si no, la de creación (created_at).
 * Retorna un Date, o null si ninguna es válida. Mismo criterio (`updated_at ??
 * created_at`) que usa getOportunidadDateStatus para `refForUpdate`.
 */
export const getOportunidadRefDate = (oportunidad) => {
  const { validCreated, validUpdated } = resolveDates(oportunidad);
  return validUpdated || validCreated;
};

export const getOportunidadDateStatus = (oportunidad) => {
  const estatus = oportunidad?.estatus;

  const { validCreated, validUpdated } = resolveDates(oportunidad);

  const refForUpdate = validUpdated || validCreated;
  const today = new Date();
  const daysSinceUpdate = refForUpdate ? daysBetween(refForUpdate, today) : null;
  const daysSinceCreated = validCreated ? daysBetween(validCreated, today) : null;
  const hasUpdate = !!validUpdated && !!validCreated && daysBetween(validCreated, validUpdated) > 0;

  const isActive = ACTIVE_ESTATUS.has(estatus);
  let severity = "none";
  if (isActive && daysSinceUpdate != null) {
    const thresholds = STATUS_THRESHOLDS[estatus];
    if (daysSinceUpdate >= thresholds.overdue) severity = "overdue";
    else if (daysSinceUpdate >= thresholds.safe) severity = "warning";
    else severity = "safe";
  }

  return {
    isActive,
    severity,
    hasUpdate,
    daysSinceUpdate,
    daysSinceCreated,
    createdLabel: formatDate(validCreated),
    updatedLabel: formatDate(validUpdated),
  };
};

/**
 * Días hasta el próximo downgrade automático (mismos umbrales "overdue" que el cron
 * downgrade_pipeline_status, docs/dominios/downgrade-pipeline.md) y a qué estatus baja. `null`
 * para estatus fuera de 50/75/90 (100%/0% no bajan) o sin fecha de referencia válida. El caller
 * decide si pinta el semáforo — `sales_btl` comparte esta misma lectura de días pero el cron no
 * la cubre (Pizarra la usa solo para mostrar los días, nunca el color).
 */
export const getDowngradeInfo = (oportunidad) => {
  const estatus = oportunidad?.estatus;
  const thresholds = STATUS_THRESHOLDS[estatus];
  const estatusDestino = NEXT_ESTATUS[estatus];
  if (!thresholds || !estatusDestino) return null;

  const { daysSinceUpdate } = getOportunidadDateStatus(oportunidad);
  if (daysSinceUpdate == null) return null;

  return {
    daysSinceUpdate,
    diasRestantes: thresholds.overdue - daysSinceUpdate,
    estatusDestino,
  };
};
